import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppDispatch, GetState, RootState } from '@SonarCX/store'

import {
  createTaskPriority,
  deleteTaskPriority,
  getTaskPriorities,
  updateTaskPriority
} from '../lib/api'
import { AddErrorAlert, AddSuccessAlert } from './alert'
import { sortByFlowOrder } from '@SonarCX/pages/Settings/TaskSettings/DraggableSetting/helpers'

type PriorityState = {
  priorities: TaskPriority[]
  loaded: boolean
  loading: boolean
  error: any
}

const initialState: PriorityState = {
  priorities: [],
  loaded: false,
  loading: false,
  error: null
}

export const prioritySlice = createSlice({
  name: 'priority',
  initialState,
  reducers: {
    loading: (state: PriorityState) => {
      state.loading = true
    },

    load: (state: PriorityState, action: PayloadAction<TaskPriority[]>) => {
      state.priorities = sortByFlowOrder(action.payload)
      state.loaded = true
      state.loading = false
    },

    add: (state: PriorityState, action: PayloadAction<TaskPriority>) => {
      const added = [...state.priorities, action.payload]
      state.priorities = sortByFlowOrder(added)
    },

    update: (state: PriorityState, action: PayloadAction<TaskPriority>) => {
      const newPriority = action.payload
      const replaced = state.priorities.map(status => {
        if (status.id === newPriority.id) {
          return newPriority
        }

        return status
      })
      state.priorities = sortByFlowOrder(replaced)
    },

    remove: (state: PriorityState, action: PayloadAction<string>) => {
      const copy = state.priorities.filter(t => t.id !== action.payload)
      state.priorities = sortByFlowOrder(copy)
    },

    failed: (state, action: PayloadAction<any>) => {
      state.error = action.payload
      state.loaded = true
      state.loading = false
    },

    patch: (
      state: PriorityState,
      action: PayloadAction<Record<string, Partial<TaskPriority>>>
    ) => {
      const data = action.payload

      const updated = state.priorities.map(status => {
        return {
          ...status,
          ...(data[status.id] ?? {})
        }
      })

      state.priorities = sortByFlowOrder(updated)
    }
  }
})

const { loading, load, add, update, remove, failed, patch } =
  prioritySlice.actions

// Asynchronous thunk action
export const fetchPriorities = () => {
  return async (dispatch: AppDispatch) => {
    dispatch(loading())

    try {
      const priorities = await getTaskPriorities()
      dispatch(load(priorities))
    } catch (error) {
      dispatch(failed(error))
    }
  }
}

export const createPriority = (status: APITaskPriorityCreate) => {
  return async (dispatch: AppDispatch) => {
    const data: APITaskPriorityCreate = { ...status }

    if (data.colour) {
      data.colour = data.colour.substring(1)
    }

    const created = await createTaskPriority(data)
    dispatch(add(created))
  }
}

export const updatePriority = (data: TaskPriority) => {
  return async (dispatch: AppDispatch) => {
    const { id } = data

    const updated = await updateTaskPriority(id, data)
    dispatch(update(updated))
  }
}

export const deletePriority = (id: string) => {
  return async (dispatch: AppDispatch) => {
    await deleteTaskPriority(id)
    dispatch(remove(id))
  }
}

export const patchPriorities = (
  data: (Partial<TaskPriority> & { id: string })[]
) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    const { priorities } = selectTaskPriority(getState())

    try {
      const toUpdate: Record<string, TaskPriority> = {}

      data.forEach(item => {
        const { id, ...props } = item
        const current = priorities.find(status => status.id === id)

        if (!current) {
          return
        }

        // Check if any of the updates are new
        const hasUpdate = Object.entries(props).some(
          ([key, val]) => current[key] !== val
        )

        if (hasUpdate) {
          toUpdate[id] = { ...current, ...item }
        }
      })

      // Send updates to server
      Object.values(toUpdate).forEach(status => {
        const { id, ...priorityToUpdate } = status

        priorityToUpdate.colour =
          priorityToUpdate.colour?.toLocaleLowerCase() ?? null

        updateTaskPriority(id, priorityToUpdate)
      })

      dispatch(patch(toUpdate))

      dispatch(AddSuccessAlert('Changes saved.'))
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }
}

export const selectTaskPriority = (state: RootState) => state.priority

export default prioritySlice.reducer
